import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Cpu, Cloud, Code, Shield, Database, Wifi, Globe, Zap, Server, Lock } from 'lucide-react';

const ICONS = [Cpu, Cloud, Code, Shield, Database, Wifi, Globe, Zap, Server, Lock];
const COLORS = [
  'rgba(0, 255, 136, 0.4)', // Neon green
  'rgba(0, 212, 255, 0.4)', // Neon cyan
  'rgba(0, 136, 255, 0.4)', // Neon blue
  'rgba(168, 85, 247, 0.4)', // Neon purple
];

const PARTICLE_COUNT = 45;

const getRandom = (min, max) => Math.random() * (max - min) + min;

// Dedicated sub-component to ensure React hooks are declared at the top level of their own component context
const SpaceParticle = ({ p, scrollY, mouse }) => {
  const IconComponent = p.icon;
  
  // Declaring scroll transformations safely at the top-level of this component instance
  const scrollTransform = useTransform(scrollY, [0, 5000], [0, -5000 * p.speedFactor]);
  const smoothScrollY = useSpring(scrollTransform, { stiffness: 80, damping: 25 });

  // Mouse displacement mapping
  const mouseX = mouse.x * p.speedFactor;
  const mouseY = mouse.y * p.speedFactor;

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: `${p.x}%`,
        top: `${p.y}%`,
        y: smoothScrollY,
        x: mouseX,
        width: p.size,
        height: p.size,
        opacity: p.opacity,
        filter: p.blur,
        zIndex: p.zIndex,
      }}
      animate={{
        rotate: [0, p.rotateSpeed],
        x: [mouseX, mouseX + p.driftX, mouseX],
      }}
      transition={{
        rotate: { duration: 15, repeat: Infinity, ease: 'linear' },
        x: { duration: 12, repeat: Infinity, ease: 'easeInOut' },
      }}
    >
      {p.zIndex === 3 ? (
        // Top-level 3D glassmorphic elements
        <div 
          className="w-full h-full rounded-xl flex items-center justify-center p-1 border border-white/10 shadow-lg shadow-black/80"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            boxShadow: `0 0 15px ${p.color}`,
          }}
        >
          <IconComponent 
            className="w-full h-full text-white" 
            style={{ filter: `drop-shadow(0 0 5px ${p.color})` }}
          />
        </div>
      ) : (
        // Mid and low-level glowing particle dots/icons
        <IconComponent 
          className="w-full h-full text-white/50" 
          style={{ filter: `drop-shadow(0 0 3px ${p.color})` }}
        />
      )}
    </motion.div>
  );
};

const GlobalSpaceBackground = () => {
  const [particles, setParticles] = useState([]);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const { scrollY } = useScroll();

  useEffect(() => {
    // Generate particles distributed in 3D-like depth lanes
    const newParticles = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
      const zIndex = getRandom(1, 3); // 3 distinct depth lanes: 1 = far, 2 = mid, 3 = close
      const size = zIndex === 3 ? getRandom(35, 55) : zIndex === 2 ? getRandom(20, 32) : getRandom(10, 18);
      const opacity = zIndex === 3 ? getRandom(0.4, 0.7) : zIndex === 2 ? getRandom(0.25, 0.5) : getRandom(0.15, 0.3);
      const blur = zIndex === 1 ? 'blur(1px)' : 'none';
      const speedFactor = zIndex === 3 ? 0.95 : zIndex === 2 ? 0.45 : 0.15; // Closer particles fly faster
      
      return {
        id: i,
        x: getRandom(2, 98),
        y: getRandom(5, 95),
        zIndex,
        size,
        opacity,
        blur,
        speedFactor,
        icon: ICONS[Math.floor(Math.random() * ICONS.length)],
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rotateSpeed: getRandom(-90, 90),
        driftX: getRandom(-15, 15),
      };
    });
    setParticles(newParticles);

    // Dynamic mouse move interaction
    const handleMouseMove = (e) => {
      setMouse({
        x: (e.clientX / window.innerWidth - 0.5) * 30, // Max 30px offset
        y: (e.clientY / window.innerHeight - 0.5) * 30,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0 overflow-hidden bg-black"
    >
      {/* Dynamic Deep Space Nebula Gradients */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(0,255,136,0.06)_0%,transparent_50%)]"></div>
      <div className="absolute top-[30%] right-[10%] w-[500px] h-[500px] bg-neon-cyan/5 rounded-full blur-[150px] animate-pulse-slow"></div>
      <div className="absolute top-[60%] left-[5%] w-[600px] h-[600px] bg-neon-purple/3 rounded-full blur-[180px] animate-pulse-slower"></div>
      <div className="absolute bottom-0 right-[20%] w-[500px] h-[500px] bg-neon-blue/4 rounded-full blur-[140px] animate-pulse-slow"></div>

      {/* Grid Overlay for futuristic mesh tech vibe */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:50px_50px] opacity-40"></div>

      {/* Render each space particle component with dynamic hooks correctly scoped */}
      {particles.map((p) => (
        <SpaceParticle 
          key={p.id} 
          p={p} 
          scrollY={scrollY} 
          mouse={mouse} 
        />
      ))}
    </div>
  );
};

export default GlobalSpaceBackground;
